import React, { useContext } from "react";
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { useTextDomainContext } from "@/app/providers";

interface LoadingPageProps {
  /** Optional custom title */
  title?: string;
  /** Optional custom message */
  message?: string;
}

/**
 * Professional Loading Page component.
 * Displays a clean, modern loading screen while verifying permissions or fetching data.
 */
export const LoadingPage: React.FC<LoadingPageProps> = ({ title, message }) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const theme = useTheme();

  const displayTitle = title || gettext("Loading...");
  const displayMessage =
    message || gettext("Verifying permissions and preparing your view...");

  return (
    <Box
      sx={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        px: 2,
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 5 },
            textAlign: "center",
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.default, 0.95)})`
                : `linear-gradient(145deg, #ffffff, ${alpha(theme.palette.primary.light, 0.04)})`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 20px 40px rgba(0,0,0,0.5)"
                : "0 20px 40px rgba(0,0,0,0.06)",
            backdropFilter: "blur(10px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Background Ambient Glow Accent */}
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 140,
              height: 140,
              borderRadius: "50%",
              background: alpha(theme.palette.primary.main, 0.1),
              filter: "blur(30px)",
              pointerEvents: "none",
            }}
          />

          <Stack
            spacing={3}
            sx={{ alignItems: "center", position: "relative", zIndex: 1 }}
          >
            {/* Glowing Spinner Container */}
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  boxShadow: `0 0 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              />
              <CircularProgress
                size={56}
                thickness={3.6}
                sx={{
                  color: theme.palette.primary.main,
                }}
              />
            </Box>

            {/* Title & Description */}
            <Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  letterSpacing: "-0.01em",
                  color: theme.palette.text.primary,
                }}
              >
                {displayTitle}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                }}
              >
                {displayMessage}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};
